import { TourFormData, ValidationError, FieldValidation } from '../types';

export const MAX_BANNER_BYTES = 10 * 1024 * 1024; // 10MB
export const MAX_BROCHURE_BYTES = 30 * 1024 * 1024; // 30MB
export const MAX_GALLERY_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB

export const isFileSizeError = (message?: string): boolean =>
  Boolean(message && /too large|must be (10MB|30MB) or smaller|must be less than \d+MB/i.test(message));

// Validation rules for each field - Only Name, Price, Departure, Arrival, Deadline, Status are required
export const fieldValidationRules: Record<keyof TourFormData, FieldValidation> = {
  name: {
    required: true,
    minLength: 3,
    maxLength: 100,
    pattern: /^[a-zA-Z0-9\s\-&,()]+$/,
  },
  tagline: {
    required: true,
    minLength: 2,
    maxLength: 120,
  },
  countries: {
    required: true, // At least 1 country is required
    custom: (value: string[]) => {
      // Check if at least one country is provided
      if (!value || value.length === 0) {
        return 'At least 1 country is required';
      }
      
      // Validate each country
      for (let i = 0; i < value.length; i++) {
        const country = value[i];
        if (!country || country.trim() === '') {
          return `Country ${i + 1} cannot be empty`;
        }
        if (country.length < 2 || country.length > 50) {
          return `Country ${i + 1} must be between 2 and 50 characters`;
        }
        if (!/^[a-zA-Z\s\-&,()]+$/.test(country)) {
          return `Country ${i + 1} contains invalid characters`;
        }
      }
      return null;
    },
  },
  price: {
    required: true,
    min: 1,
    max: 1000000,
  },
  groupSize: {
    required: false,
    min: 1,
    max: 100,
  },
  duration: {
    required: false, // Made optional
    min: 1,
    max: 365,
  },
  departure: {
    required: true,
    custom: (value: string) => {
      const date = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date < today) {
        return 'Departure date must be in the future';
      }
      return null;
    },
  },
  arrival: {
    required: true,
    custom: (value: string) => {
      const date = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date < today) {
        return 'Arrival date must be in the future';
      }
      return null;
    },
  },
  deadline: {
    required: true,
    custom: (value: string) => {
      const date = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date < today) {
        return 'Deadline must be in the future';
      }
      return null;
    },
  },
  availability: {
    required: false, // Internal tracking only
    max: 100,
  },
  showUrgencyBadge: {
    required: false,
  },
  urgencySlots: {
    required: false,
    min: 1,
    max: 20,
  },
  description: {
    required: false, // Made optional
    minLength: 10,
    maxLength: 2000,
  },
  banner: {
    required: false, // Made optional
    custom: (value: File | null) => {
      if (value && !value.type.startsWith('image/')) {
        return 'Banner must be an image file';
      }
      if (value && value.size > MAX_BANNER_BYTES) {
        return 'Banner image must be 10MB or smaller';
      }
      return null;
    },
  },
  brochure: {
    required: false, // Made optional
    custom: (value: File | null) => {
      if (value && !value.type.includes('pdf')) {
        return 'Brochure must be a PDF file';
      }
      if (value && value.size > MAX_BROCHURE_BYTES) {
        return 'Brochure must be 30MB or smaller';
      }
      return null;
    },
  },
  status: {
    required: true,
  },
};

// Validate a single field
export const validateField = (
  fieldName: keyof TourFormData,
  value: any,
  rules: FieldValidation
): string | null => {
  // Required check
  if (rules.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
    return `${fieldName} is required`;
  }

  // Skip other validations if value is empty and not required
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return null;
  }

  // String length validations
  if (typeof value === 'string') {
    if (rules.minLength && value.length < rules.minLength) {
      return `${fieldName} must be at least ${rules.minLength} characters`;
    }
    if (rules.maxLength && value.length > rules.maxLength) {
      return `${fieldName} must be no more than ${rules.maxLength} characters`;
    }
    if (rules.pattern && !rules.pattern.test(value)) {
      return `${fieldName} contains invalid characters`;
    }
  }

  // Number validations
  if (typeof value === 'number') {
    if (rules.min !== undefined && value < rules.min) {
      return `${fieldName} must be at least ${rules.min}`;
    }
    if (rules.max !== undefined && value > rules.max) {
      return `${fieldName} must be no more than ${rules.max}`;
    }
  }

  // Custom validation
  if (rules.custom) {
    return rules.custom(value);
  }

  return null;
};

// Validate entire form
export const validateForm = (formData: TourFormData): ValidationError[] => {
  const errors: ValidationError[] = [];

  Object.entries(fieldValidationRules).forEach(([fieldName, rules]) => {
    const value = formData[fieldName as keyof TourFormData];
    const error = validateField(fieldName as keyof TourFormData, value, rules);
    
    if (error) {
      errors.push({
        field: fieldName,
        message: error,
      });
    }
  });

  // Cross-field validation: arrival must be after departure
  if (formData.departure && formData.arrival) {
    const departureDate = new Date(formData.departure);
    const arrivalDate = new Date(formData.arrival);
    
    if (arrivalDate <= departureDate) {
      errors.push({
        field: 'arrival',
        message: 'Arrival date must be after departure date',
      });
    }
  }

  // Cross-field validation: availability cannot exceed group size (only if both are provided)
  if (formData.availability && formData.groupSize && formData.availability > formData.groupSize) {
    errors.push({
      field: 'availability',
      message: 'Availability cannot exceed group size',
    });
  }

  if (formData.showUrgencyBadge && (!formData.urgencySlots || formData.urgencySlots < 1)) {
    errors.push({
      field: 'urgencySlots',
      message: 'Enter how many slots to show on the website',
    });
  }

  // Duration validation: nights will be automatically calculated as days-1

  return errors;
};

// Get field examples
export const getFieldExamples = (): Record<keyof TourFormData, string> => ({
  name: 'Bali Adventure Tour',
  tagline: 'An island escape through temples and coastline',
  countries: 'Indonesia (required), Thailand (optional - click + to add more)',
  price: '45000',
  groupSize: '25 (optional, internal only)',
  duration: '5 (optional - will show as 5D/4N automatically)',
  departure: '2024-06-15',
  arrival: '2024-06-22',
  deadline: '2024-05-15',
  availability: '20 (optional, internal only)',
  showUrgencyBadge: 'Off unless you want a public urgency badge',
  urgencySlots: '3 (shown as “Only 3 slots left” — marketing only)',
  description: 'Enter detailed tour description in Markdown format',
  banner: 'Upload a high-quality image (JPG, PNG) - max 10MB (optional)',
  brochure: 'Upload tour brochure (PDF) - max 30MB (optional)',
  status: 'active',
});
